import { QueryClient } from '@tanstack/react-query';
import { normalizeHm, resolvedEventBookingType, TDR } from 'tdr-common';
import { buildEventBookNowUrl } from './buildEventBookNowUrl';
import {
	getCachedEventBookNow,
	getEventBookNowCacheKey,
	setCachedEventBookNow
} from './eventBookNowCache';
import { findFirstAvailableTime, findFirstAvailableTimeInWindow } from './getFirstAvailableDate';
import { getEventCalendarTables, resolveEventBookNowGroupSize } from './resolveEventBookNowGroupSize';

/** Only called for param events (table events are cached without a slot scan above). */
function resolveFirstTimeFromSlots(
	slotsData: TDR.Availability.getServicesOutput | undefined,
	paramHasTimeConstraint: boolean,
	betweenStart: string | undefined,
	betweenEnd: string | undefined
): string | undefined {
	if (!slotsData) {
		return undefined;
	}

	if (paramHasTimeConstraint && betweenStart !== undefined) {
		return findFirstAvailableTimeInWindow(slotsData, betweenStart, betweenEnd ?? betweenStart);
	}

	return findFirstAvailableTime(slotsData);
}

/**
 * After calendar/timeslot prefetch, compute the book-now URL once and store it
 * in the session cache so event cards render instantly on navigation.
 */
export function cacheEventBookNowFromPrefetch(
	queryClient: QueryClient,
	event: TDR.Event,
	tables: TDR.Table[],
	restaurantId: string,
	restaurantSlug: string,
	firstDate: string | undefined
): void {
	const cacheKey = getEventBookNowCacheKey(restaurantId, event.id);
	if (getCachedEventBookNow(cacheKey)?.bookNowHref !== null) {
		return;
	}

	const evKind = resolvedEventBookingType(event);

	// Table events need no availability — cache the bare table URL immediately.
	if (evKind === 'table' && event.linkedTableSlug) {
		const href = buildEventBookNowUrl(event, restaurantSlug, tables, null, null) ?? null;
		if (href) {
			setCachedEventBookNow(cacheKey, { bookNowHref: href });
		}
		return;
	}

	// Only param events reach here (table events return above), so no linked table to resolve.
	const calendarTables = getEventCalendarTables(event, tables);
	const betweenStart = normalizeHm(event.condition?.value?.betweenTimes?.[0]);
	const betweenEnd = normalizeHm(event.condition?.value?.betweenTimes?.[1]);
	const paramHasTimeConstraint = betweenStart !== undefined;

	if (!firstDate) {
		return;
	}

	let firstTime: string | null = null;

	if (paramHasTimeConstraint) {
		const groupSize = resolveEventBookNowGroupSize(event, tables);
		const slotsInput: TDR.Availability.getServicesInput = {
			tableIds: calendarTables.map((t) => t.id),
			dateStr: firstDate,
			restaurantId,
			options: { groupSize }
		};
		const slotsData = queryClient.getQueryData<TDR.Availability.getServicesOutput>(
			['serviceTimeslots', slotsInput]
		);
		firstTime = resolveFirstTimeFromSlots(slotsData, paramHasTimeConstraint, betweenStart, betweenEnd) ?? null;
	}

	const href = buildEventBookNowUrl(event, restaurantSlug, tables, firstDate, firstTime) ?? null;
	if (href) {
		setCachedEventBookNow(cacheKey, { bookNowHref: href });
	}
}
